/**
 * Backup scheduler (spec section 12).
 *
 *  - Dexie `creating`/`updating` hooks on the bookkeeping tables bump
 *    `driveState.pendingChanges` (via an in-memory counter flushed outside
 *    the writing transaction) and arm a 20 s debounce.
 *  - On `start()`: back up every connected company with pending changes or a
 *    last backup older than 24 h.
 *  - Monthly frozen copies and the year-end copy are handled by `writeBackup`.
 *  - Only companies whose remote tree exists (`driveState.folders`) are backed
 *    up automatically, and only while the store reports itself authorised.
 *    Nothing here ever opens a sign-in dialog; an unauthorised store sets
 *    `needsSignIn` for the pill.
 *
 * `createBackupScheduler` takes injectable deps for tests; `backupScheduler`
 * is the app-wide instance on the singleton database.
 */
import { remoteStore } from '@/cloud/store';
import { db as defaultDb, type BookkeepingDB } from '@/db/db';
import type { BackupKind, DriveState, Uuid } from '@/domain/types';
import { isRestoring, writeBackup, type WriteBackupOptions } from './backup';
import { withLedgerLock } from './ledger';
import { isMerging } from './merge';
import { getDriveState, updateDriveState } from './tree';

export const HOOKED_TABLES = [
  'companies',
  'companySettings',
  'fiscalYears',
  'accounts',
  'accountEvents',
  'verifikationer',
  'paymentSources',
  'periodLocks',
] as const;
export type HookedTable = (typeof HOOKED_TABLES)[number];

export const DEBOUNCE_MS = 20_000;
export const STALE_AFTER_MS = 24 * 60 * 60_000;

export interface SchedulerDeps {
  db: BookkeepingDB;
  writeBackup: (companyId: Uuid, kind: BackupKind, db: BookkeepingDB, opts?: WriteBackupOptions) => Promise<unknown>;
  /** False when the store would have to prompt the user to sign in. */
  isAuthorized: () => Promise<boolean>;
  /** True while rows are being written that are not this device's changes (a restore, a merge). */
  isRestoring: () => boolean;
  now: () => number;
  setTimer: (fn: () => void, ms: number) => unknown;
  clearTimer: (handle: unknown) => void;
  debounceMs: number;
  staleAfterMs: number;
}

/** Which company a hooked row belongs to. */
export function companyIdOf(table: HookedTable, obj: unknown): Uuid | null {
  if (!obj || typeof obj !== 'object') return null;
  const row = obj as { id?: unknown; companyId?: unknown };
  const id = table === 'companies' ? row.id : row.companyId;
  return typeof id === 'string' ? id : null;
}

/** Pure: should the company be backed up on app open? */
export function dueOnOpen(state: DriveState, now: number, staleAfterMs: number = STALE_AFTER_MS): boolean {
  if (!state.folders) return false;
  if (state.pendingChanges > 0) return true;
  if (!state.lastBackupAt) return true;
  return now - Date.parse(state.lastBackupAt) > staleAfterMs;
}

export function createBackupScheduler(partial: Partial<SchedulerDeps> = {}) {
  const deps: SchedulerDeps = {
    db: partial.db ?? defaultDb,
    // Under the company's ledger lock, so a backup never interleaves with a
    // booking's pull-and-publish or the watcher's merge.
    writeBackup: partial.writeBackup ?? ((companyId, kind, db, opts) => withLedgerLock(companyId, db, () => writeBackup(companyId, kind, db, undefined, opts))),
    isAuthorized: partial.isAuthorized ?? (async () => (await remoteStore()?.isAuthorized()) ?? false),
    // Merged rows came from the remote; counting them would push them straight back.
    isRestoring: partial.isRestoring ?? (() => isRestoring() || isMerging()),
    now: partial.now ?? (() => Date.now()),
    setTimer: partial.setTimer ?? ((fn, ms) => setTimeout(fn, ms)),
    clearTimer: partial.clearTimer ?? ((h) => clearTimeout(h as ReturnType<typeof setTimeout>)),
    debounceMs: partial.debounceMs ?? DEBOUNCE_MS,
    staleAfterMs: partial.staleAfterMs ?? STALE_AFTER_MS,
  };

  const dirty = new Map<Uuid, number>();
  const debounce = new Map<Uuid, unknown>();
  const running = new Map<Uuid, Promise<void>>();
  let flushTimer: unknown = null;
  let flushPromise: Promise<void> | null = null;
  let started = false;
  let startPromise: Promise<void> | null = null;
  let unsubscribeHooks: (() => void) | null = null;

  /** Persist the in-memory counters to `driveState` (outside any Dexie transaction). */
  async function persistDirty(): Promise<void> {
    const batch = new Map(dirty);
    dirty.clear();
    const at = new Date(deps.now()).toISOString();
    for (const [companyId, count] of batch) {
      try {
        await updateDriveState(
          companyId,
          (s) => {
            s.pendingChanges += count;
            s.dirtySince ??= at;
          },
          deps.db,
        );
      } catch {
        // The row is re-counted on the next change; never block bookkeeping.
      }
    }
  }

  function scheduleFlush(): void {
    if (flushTimer !== null) return;
    flushTimer = deps.setTimer(() => {
      flushTimer = null;
      flushPromise = persistDirty().finally(() => {
        flushPromise = null;
      });
    }, 0);
  }

  async function runAuto(companyId: Uuid): Promise<void> {
    const state = await getDriveState(companyId, deps.db);
    if (!state.folders) return;
    if (!(await deps.isAuthorized())) {
      await updateDriveState(companyId, (s) => void (s.needsSignIn = true), deps.db);
      return;
    }
    try {
      await deps.writeBackup(companyId, 'auto', deps.db);
    } catch {
      // Logged in backupLog / driveState.lastError by writeBackup.
    }
  }

  function run(companyId: Uuid, job: () => Promise<void>): Promise<void> {
    const previous = running.get(companyId) ?? Promise.resolve();
    const next = previous.then(job, job).finally(() => {
      if (running.get(companyId) === next) running.delete(companyId);
    });
    running.set(companyId, next);
    return next;
  }

  function armDebounce(companyId: Uuid): void {
    const existing = debounce.get(companyId);
    if (existing !== undefined) deps.clearTimer(existing);
    debounce.set(
      companyId,
      deps.setTimer(() => {
        debounce.delete(companyId);
        void run(companyId, () => runAuto(companyId));
      }, deps.debounceMs),
    );
  }

  function markDirty(companyId: Uuid): void {
    dirty.set(companyId, (dirty.get(companyId) ?? 0) + 1);
    scheduleFlush();
    if (started) armDebounce(companyId);
  }

  function installHooks(): () => void {
    const offs: (() => void)[] = [];
    for (const name of HOOKED_TABLES) {
      const table = deps.db.table(name);
      const creating = (_key: unknown, obj: unknown) => {
        if (deps.isRestoring()) return;
        const id = companyIdOf(name, obj);
        if (id) markDirty(id);
      };
      const updating = (_mods: unknown, _key: unknown, obj: unknown) => {
        if (deps.isRestoring()) return;
        const id = companyIdOf(name, obj);
        if (id) markDirty(id);
      };
      table.hook('creating', creating);
      table.hook('updating', updating);
      offs.push(() => {
        table.hook('creating').unsubscribe(creating);
        table.hook('updating').unsubscribe(updating);
      });
    }
    return () => offs.forEach((off) => off());
  }

  return {
    /** Install hooks and back up whatever is due. Idempotent. */
    start(): void {
      if (started) return;
      started = true;
      unsubscribeHooks = installHooks();
      startPromise = (async () => {
        const states = await deps.db.driveState.toArray();
        for (const state of states) {
          if (dueOnOpen(state, deps.now(), deps.staleAfterMs)) void run(state.companyId, () => runAuto(state.companyId));
        }
      })().catch(() => undefined);
    },
    stop(): void {
      started = false;
      unsubscribeHooks?.();
      unsubscribeHooks = null;
      for (const handle of debounce.values()) deps.clearTimer(handle);
      debounce.clear();
      if (flushTimer !== null) deps.clearTimer(flushTimer);
      flushTimer = null;
    },
    /** Count a change and (when started) arm the 20 s debounce. */
    markDirty,
    /** Manual / year-end / monthly backups; errors propagate to the caller. */
    async runNow(companyId: Uuid, kind: BackupKind, opts?: WriteBackupOptions): Promise<void> {
      const existing = debounce.get(companyId);
      if (existing !== undefined) {
        deps.clearTimer(existing);
        debounce.delete(companyId);
      }
      await this.flushPending();
      await run(companyId, async () => {
        await deps.writeBackup(companyId, kind, deps.db, opts);
      });
    },
    status(companyId: Uuid): Promise<DriveState> {
      return getDriveState(companyId, deps.db);
    },
    /** Wait until in-memory counters are written to `driveState` (tests, before runNow). */
    async flushPending(): Promise<void> {
      if (flushTimer !== null) {
        deps.clearTimer(flushTimer);
        flushTimer = null;
        flushPromise = persistDirty().finally(() => {
          flushPromise = null;
        });
      }
      if (flushPromise) await flushPromise;
    },
    /** Wait for any backup currently running for the company (tests). */
    async idle(companyId?: Uuid): Promise<void> {
      if (startPromise) await startPromise;
      await this.flushPending();
      const jobs = companyId ? [running.get(companyId)] : [...running.values()];
      await Promise.all(jobs.map((j) => j?.catch(() => undefined)));
    },
  };
}

export type BackupScheduler = ReturnType<typeof createBackupScheduler>;

export const backupScheduler: BackupScheduler = createBackupScheduler();
